import type { BondRenderField } from "@/lib/bondFields";
import {
  bondFlowOf,
  type BondFlow,
  type BondSubmissionStatus,
} from "@/lib/brokerBonds";
import { formatBondValue } from "@/lib/bondValueDisplay";
import { sentenceCaseKey } from "@/lib/listFormat";

// BRK-S140 — how a filed bond order's terms READ as a record.
//
// The final step captures twenty-odd values in one flat, configuration-driven
// bag. Rendered in arrival order that is a form somebody has already filled in;
// what an underwriter and a broker reading the same order need is the four
// questions it answers: who the bond is FOR, what the WORK is, what the BOND's
// own terms are, and anything else the configuration asked for.
//
// ── WHY A RULE AND NOT A LIST ────────────────────────────────────────────────
//
// The field set is discovery's, and it grows without a portal release — that is
// the whole point of a configuration-driven step. So a hand-maintained
// `["obligeeName", "obligeeType", …]` would silently DROP the first field Greg
// adds, or worse, drop it into no section at all. Instead the classification is
// a small set of key rules plus a GENERIC FALLBACK, and the fallback is the part
// that matters: an unclassified field is still rendered, in its own section,
// rather than disappearing because nobody taught this module about it.
//
// Deliberately keyed on the FIELD KEY and not on the label, which is display and
// changes (PMS-S317 relabelled `bondAmount` to "Bond penalty" and kept the key
// precisely because the key is contract). And deliberately not on the TYPE: two
// currency fields can belong to different sections, and one section holds three
// types.

/** The sections, in the order a broker reads them. */
export type BondProfileSectionId = "obligee" | "project" | "terms" | "other";

export interface BondProfileSection {
  id: BondProfileSectionId;
  title: string;
  fields: BondRenderField[];
}

const TITLES: Record<BondProfileSectionId, string> = {
  obligee: "Obligee",
  project: "Project",
  terms: "Bond terms",
  // Named for what it IS rather than "Other": a broker reading a section called
  // "Other" cannot tell whether it is a catch-all or a category Signal has.
  other: "Additional details",
};

/**
 * Fields whose section the KEY does not give away, stated outright.
 *
 * Short on purpose — every entry here is a field the rules below would put
 * somewhere defensible but wrong, and a long table would be the hand-maintained
 * list this module exists to avoid.
 */
const EXPLICIT: Record<string, BondProfileSectionId> = {
  // The price of the WORK, not a term of the bond: it is what the broker bid,
  // which is also why the bid-result panel measures against it (BRK-S141).
  contractPrice: "project",
  estimatedContractPrice: "project",
  estContractPrice: "project",
  bidAmount: "project",
  // A bid DATE is about the job going out to tender, not about the bond's term.
  bidDate: "project",
  startDate: "project",
  completionDate: "project",
};

/**
 * The key rules, IN ORDER — the first match wins, so a rule for a narrower
 * subject is listed above a broader one. Matched case-insensitively.
 *
 * Most are PREFIX rules, because a field's key leads with its subject. `bond` is
 * the one CONTAINMENT rule, and it earns the exception: `estimatedBondAmount`
 * leads with "estimated" and is unmistakably a bond term, and so would
 * `revisedBondAmount` or `finalBondPenalty`. Containment is safe HERE precisely
 * because it is ordered below `project` — `projectBondNotes` is decided by the
 * project rule first, which is why the ordering is part of the rule and not a
 * tidying detail.
 */
type KeyRule = readonly [
  pattern: string,
  section: BondProfileSectionId,
  mode?: "contains",
];

const RULES: readonly KeyRule[] = [
  ["obligee", "obligee"],
  ["project", "project"],
  ["liquidateddamages", "terms"],
  ["maintenance", "terms"],
  ["contract", "terms"],
  ["bond", "terms", "contains"],
  ["work", "terms"],
];

/**
 * Which section one field belongs to.
 *
 * Falls through to `other` rather than guessing: an unclassified field is a
 * field this build has not been taught about, and rendering it in a named
 * section it does not belong to is worse than rendering it honestly in a
 * catch-all.
 */
export function sectionForField(key: string): BondProfileSectionId {
  const explicit = EXPLICIT[key];
  if (explicit) return explicit;
  const lower = key.toLowerCase();
  for (const [pattern, section, mode] of RULES) {
    const hit =
      mode === "contains" ? lower.includes(pattern) : lower.startsWith(pattern);
    if (hit) return section;
  }
  return "other";
}

/**
 * The final step's fields, grouped into sections — CONFIGURATION ORDER within
 * each, so the record reads like the form the broker filled in.
 *
 * TWO KINDS OF EMPTY ARE DROPPED, and that is the one thing this decides beyond
 * the grouping (which is why the caller gets a list rather than a record):
 *
 *   • a section the configuration declared no fields for;
 *   • when `values` is given, a section whose fields are ALL UNANSWERED. A bond
 *     configuration asks twenty-odd mostly-optional questions, so a filed order
 *     routinely leaves a whole section blank — and a heading over "nothing
 *     captured" reads as data that failed to load rather than as a question
 *     nobody needed to answer.
 *
 * "Answered" is decided by `formatBondValue` returning a string, which is the
 * SAME rule the record itself omits a row by (`CapturedValues`). Deliberately
 * the same call rather than a local re-statement: two rules for "is this
 * answered" is how a section gets dropped while one of its rows would have
 * rendered, or kept while none of them do.
 */
export function bondProfileSections(
  fields: readonly BondRenderField[],
  /** The captured bag. Omit to group WITHOUT dropping unanswered sections. */
  values?: Readonly<Record<string, unknown>>,
): BondProfileSection[] {
  const order: BondProfileSectionId[] = [
    "obligee",
    "project",
    "terms",
    "other",
  ];
  const grouped = new Map<BondProfileSectionId, BondRenderField[]>(
    order.map((id) => [id, []]),
  );
  for (const field of fields) {
    grouped.get(sectionForField(field.name))?.push(field);
  }
  const answered = (field: BondRenderField) =>
    values === undefined ||
    formatBondValue(values[field.name], {
      type: field.type,
      options: field.options,
    }) !== null;

  return order
    .map((id) => ({ id, title: TITLES[id], fields: grouped.get(id) ?? [] }))
    .filter((section) => section.fields.some(answered));
}

/**
 * The one field whose value is the PROJECT's address, or null when the
 * configuration declares none.
 *
 * Named by its section and its TYPE rather than by a key literal: the map link
 * belongs to whichever address field is about the project, and a configuration
 * that spells it `projectSite` still gets one. The obligee's address does not —
 * a broker has no reason to drive to the county offices.
 *
 * The FIRST match, because a configuration declaring two project addresses has
 * no way to say which is the site, and two map links would be two guesses.
 */
export function projectAddressField(
  fields: readonly BondRenderField[],
): BondRenderField | null {
  return (
    fields.find(
      (field) =>
        field.type === "address" && sectionForField(field.name) === "project",
    ) ?? null
  );
}

// ── The status timeline (BRK-S140) ───────────────────────────────────────────
//
// Greg's wireframe calls it "Track / Update Progress". Built from WHAT THE ORDER
// VIEW EXPOSES and nothing else — there is no progress field on the wire, and
// inventing one would mean a second answer to "where is this order" that could
// disagree with the status badge beside it.
//
// Four milestones at most, and every one of them is a fact the view states:
// the order exists, its details are in, it is filed, and — only when the order
// says so — it is with an underwriter.

/** Where one milestone stands. `todo` is deliberately not "failed": an order
 *  still being filled in has not failed to be filed. */
export type BondMilestoneState = "done" | "current" | "todo";

export interface BondMilestone {
  id: string;
  label: string;
  state: BondMilestoneState;
}

/** The subset of an order the timeline reads — narrowed so the rule is pure and
 *  a caller cannot accidentally make it depend on more. */
export interface BondProgressInput {
  status: BondSubmissionStatus;
  referred: boolean;
  /** `null` when the product does not pre-qualify; ABSENT for a PMS that
   *  predates the block. Both mean "no pre-qualification milestone", which is
   *  why they collapse here — see `BondSubmission.preapproval`. */
  preapproval?: { status: "approved" | "not_granted" | "pending" } | null;
  /**
   * Which flow this order ran (BRK-S160). Absent is `bond`, so every order this
   * portal has ever rendered keeps exactly the milestones it had.
   */
  flow?: BondFlow;
}

/**
 * The order's milestones, in order.
 *
 * The PRE-QUALIFICATION milestone appears only when the order carries a
 * preapproval block. A product that does not pre-qualify is a normal outcome,
 * and a greyed-out step for work that was never part of this order would be a
 * claim about the flow that nobody made — the same reasoning that makes
 * BRK-S134 OMIT the two wizard chips rather than tick them.
 *
 * A REFERRED order gets its own milestone rather than a badge on "Filed",
 * because referral is orthogonal to status: a draft can be referred and a filed
 * order can not be.
 */
export function bondOrderProgress(order: BondProgressInput): BondMilestone[] {
  const filed = order.status === "submitted";
  const milestones: BondMilestone[] = [
    { id: "started", label: "Request started", state: "done" },
  ];

  // A CANCELLED order (BRK-S181) went nowhere after it started: the broker
  // discarded it to start over. Any milestone after "started" would be a step
  // it is never going to take, so the timeline says what happened and stops.
  if (order.status === "cancelled") {
    milestones.push({ id: "cancelled", label: "Cancelled", state: "done" });
    return milestones;
  }

  if (order.preapproval != null) {
    milestones.push({
      id: "prequalification",
      label: "Pre-qualification",
      // `pending` is the ONLY unsettled member, and it is not only the pre-run
      // state: PMS mirrors the decision onto the order after the run settles,
      // so a read landing in that gap says pending for an order whose checks
      // are done. Either way the honest rendering is "in progress".
      state: order.preapproval.status === "pending" ? "current" : "done",
    });
  }

  // ── The STANDALONE pre-approval's timeline ends here (BRK-S160) ───────────
  //
  // There are no bond details to collect and nothing is filed WITH anybody: the
  // broker asked for a pre-qualification, it landed, and the decision lives on
  // the insured's Surety program block. "Bond details" would be a step nobody
  // is ever going to take, and "Filed with Signal" would leave them waiting for
  // an answer they already have.
  if (bondFlowOf(order) === "preapproval") {
    milestones.push({
      id: "complete",
      label: "Complete",
      state: filed ? "done" : "current",
    });
  } else {
    milestones.push({
      id: "details",
      label: "Bond details",
      state: filed ? "done" : "current",
    });
    milestones.push({
      id: "filed",
      label: "Filed with Signal",
      state: filed ? "done" : "todo",
    });
  }

  if (order.referred) {
    milestones.push({
      id: "review",
      label: "Underwriting review",
      // In progress once filed; still ahead of the broker while they are
      // finishing the request. Never "done" — this portal is not told when an
      // underwriter finishes, so claiming it would be inventing the one fact
      // the timeline cannot read.
      state: filed ? "current" : "todo",
    });
  }

  return milestones;
}

// ── The order's documents, grouped by the field that collected them ──────────
// BRK-S146.

/** One group of a bond order's documents: the field that asked for them, as it
 *  is labelled on screen, and what was attached. */
export interface BondDocumentGroup<T extends BondDocumentListItem> {
  /** The configuration field's key, or `NO_FIELD_KEY` for the group of
   *  documents no intake field collected. A stable React key, not a path. */
  fieldKey: string;
  label: string;
  documents: T[];
}

/** The group key for documents with no `fieldKey`. Not a configuration path —
 *  a field key is a bare identifier, so this cannot collide with one. */
export const NO_FIELD_KEY = "(no field)";

/** The half of a stored document this module needs — declared here rather than
 *  imported so the grouping rule stays pure and testable with plain objects. */
export interface BondDocumentListItem {
  documentId: string;
  filename: string;
  /** NULL for a document no `files` control collected (PMS-S334) — see
   *  `NO_FIELD_GROUP_LABEL`. */
  fieldKey: string | null;
  /** PMS's document type key — the heading for a document with no field. */
  type: string;
}

/**
 * The heading for a document no intake FIELD collected, when its TYPE cannot be
 * named either.
 *
 * `GET …/documents` is *the order's documents*, not *the order's intake
 * uploads*, so it carries the signed contract (PMS-S316) and later the issued
 * bond with `fieldKey: null`. Those belong on this section — they are part of
 * the order's file — but they answer no question the broker was asked, so they
 * cannot sit under a field's label. They get a group per TYPE instead, headed
 * by PMS's own label for it ("Signed contract"), which is both the accurate
 * heading and the one the account Documents tab uses for the same row.
 *
 * This constant is the last resort for a type nothing can label. Deliberately
 * NOT "Other": a heading called "Other" tells a broker nothing about whether it
 * is a catch-all or a category Signal has (the same argument the catch-all
 * section title makes above).
 */
export const NO_FIELD_GROUP_LABEL = "Also on this request";

/**
 * A bond order's documents, grouped by the CONFIGURATION FIELD that collected
 * them, in the order the configuration declares those fields.
 *
 * Grouped by the field rather than by the document TYPE, and the distinction is
 * the point: the type is what Signal filed it as, the field is what the broker
 * was ASKED for. On a profile whose whole job is "read back what you filed",
 * the question is the useful heading — and it is the one the configuration can
 * change without a portal release, which is why the label comes from the field
 * and not from a map here.
 *
 * A document whose `fieldKey` matches no declared field is NOT dropped. It gets
 * its own group under a humanised key, because the alternative is a file the
 * broker uploaded and can no longer see: a configuration snapshot moves on, a
 * field gets renamed, and the document is still theirs. Those groups come last,
 * in first-seen order.
 *
 * A NULL `fieldKey` groups last of all, and BY TYPE — one group per type, headed
 * by PMS's own label for it. So the signed contract reads under "Signed
 * contract" and the issued bond will read under "Bond", which is both accurate
 * and the same wording the account Documents tab gives the same row. Lumping
 * them into one bucket would have been a heading that names nothing.
 *
 * `typeLabel` is injected rather than imported so this stays pure: the labels
 * are PMS's, read by a hook one layer up.
 */
export function bondDocumentGroups<T extends BondDocumentListItem>(
  documents: readonly T[],
  fields: readonly BondRenderField[],
  typeLabel: (type: string) => string = (type) => sentenceCaseKey(type),
): BondDocumentGroup<T>[] {
  const byKey = new Map<string, T[]>();
  const byType = new Map<string, T[]>();
  for (const document of documents) {
    if (document.fieldKey === null) {
      const held = byType.get(document.type);
      if (held) held.push(document);
      else byType.set(document.type, [document]);
      continue;
    }
    const held = byKey.get(document.fieldKey);
    if (held) held.push(document);
    else byKey.set(document.fieldKey, [document]);
  }

  const groups: BondDocumentGroup<T>[] = [];
  // Declared fields first, in the configuration's own order — so the section
  // reads in the same order the broker filled it in.
  for (const field of fields) {
    const held = byKey.get(field.name);
    if (!held) continue;
    groups.push({ fieldKey: field.name, label: field.label, documents: held });
    byKey.delete(field.name);
  }
  // Then whatever is left: a field this configuration snapshot no longer
  // declares. Insertion order, which is the server's newest-first ordering.
  for (const [fieldKey, held] of byKey) {
    groups.push({
      fieldKey,
      label: humanizeFieldKey(fieldKey),
      documents: held,
    });
  }
  // Last of all: the documents no intake field asked for, one group per type.
  for (const [type, held] of byType) {
    const label = typeLabel(type);
    groups.push({
      // Keyed by type under a prefix no configuration path can spell, so a
      // field literally called `final_contract` could not collide with it.
      fieldKey: `${NO_FIELD_KEY}:${type}`,
      label: label === "" ? NO_FIELD_GROUP_LABEL : label,
      documents: held,
    });
  }
  return groups;
}

/**
 * A field key as a heading — the last-resort label for a group whose field the
 * configuration no longer declares.
 *
 * Delegates to the shared `sentenceCaseKey`: field keys are camelCase and
 * document type keys are snake_case, and both want the same sentence style, so
 * a second regex here would be the near-copy that eventually differs by a
 * hyphen.
 */
export function humanizeFieldKey(key: string): string {
  return sentenceCaseKey(key);
}
